import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, X } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import PublishPanel from "./PublishPanel";
import { StepInfo } from "./wizard/StepInfo";
import { StepContent } from "./wizard/StepContent";
import { RENALIFE_INFO } from "./wizard/shared";
import { useCampaignContent } from "./wizard/useCampaignContent";

export default function CampaignWizard({ funnelLink, onDone, onCancel, initialType }) {
  const { user } = useAuth();
  const isRenalife = user?.pin_type === "renalife";
  const [step, setStep] = useState(2);
  const type = initialType;
  const [title, setTitle] = useState("");
  const [sourceType, setSourceType] = useState(null);
  const [url, setUrl] = useState("");
  const [infoText, setInfoText] = useState("");
  const [brand, setBrand] = useState("");
  const [objective, setObjective] = useState("");
  const [pageImages, setPageImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [gc, setGc] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isRenalife) {
      setSourceType("manual");
      setTitle((t) => t || "Campaña RENALIFE MÉXICO");
      setInfoText((v) => v || RENALIFE_INFO);
      setBrand((b) => b || "RENALIFE");
    }
  }, [isRenalife]);

  const {
    textContent, loadingText, genText,
    imagePrompt, setImagePrompt, images, loadingImage, genImage,
    addPageImage, addUploadedImage, videos, setVideos, loadingVideo, genVideo,
  } = useCampaignContent(gc);
  const [includeLegend, setIncludeLegend] = useState(true);

  const setLegend = async (val) => {
    setIncludeLegend(val);
    if (!gc) return;
    try {
      await api.put(`/guided/campaigns/${gc.gc_id}/legend`, { include: val });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        if (f.type.startsWith("text/") || /\.(txt|md|csv)$/i.test(f.name)) {
          const text = (await f.text()).trim();
          setInfoText((p) => (p ? p + "\n\n" : "") + text);
          toast.success(`Texto de "${f.name}" agregado`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        if (data.kind === "image") {
          setUploadedImages((p) => [...p, { url: data.url, fileId: data.file_id }]);
          toast.success("Imagen subida");
        } else if (data.kind === "video") {
          setUploadedVideo({ url: data.url, filename: f.name });
          toast.success("Video subido. Se adjuntará a tu campaña.");
        } else if (data.kind === "text") {
          setInfoText((p) => (p ? p + "\n\n" : "") + (data.text || ""));
          toast.success("Texto agregado");
        }
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      }
    }
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };

  const extract = async () => {
    if (!url.trim()) return toast.error("Escribe la página web");
    setExtracting(true);
    try {
      const { data } = await api.post("/guided/extract", { url });
      setInfoText(data.info_text);
      setBrand(data.brand || "");
      setPageImages(data.images || []);
      toast.success("Información extraída. Revísala y edítala si quieres.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  const createCampaign = async () => {
    if (!infoText.trim()) return toast.error("Falta la información de la campaña");
    setCreating(true);
    try {
      const { data } = await api.post("/guided/campaigns", {
        type, title: title || "Mi campaña", source_type: sourceType || "manual",
        source_url: url, info_text: infoText, brand, objective,
      });
      setGc(data);
      if (uploadedVideo) {
        try {
          const r = await api.post(`/guided/campaigns/${data.gc_id}/video-from-url`, { video_url: uploadedVideo.url });
          setVideos((p) => [...p, r.data]);
        } catch (err) { console.warn("No se pudo adjuntar el video subido; podrá generarse en el paso 3:", err?.message); }
      }
      setStep(3);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const resolvedCampaign = gc ? {
    ...gc,
    text: textContent ? { text: textContent.text } : gc.text,
    images, videos,
  } : null;

  const wizard = {
    title, setTitle, sourceType, setSourceType, url, setUrl, infoText, setInfoText,
    brand, setBrand, objective, setObjective, extracting, extract, isRenalife,
    uploading, uploadedImages, uploadedVideo, dragOver, setDragOver, onDrop, handleFiles,
    creating, createCampaign, onBack: onCancel,
    includeLegend, setLegend, textContent, loadingText, genText,
    imagePrompt, setImagePrompt, images, loadingImage, genImage,
    pageImages, addPageImage, addUploadedImage, videos, loadingVideo, genVideo,
  };

  return (
    <div data-testid="campaign-wizard" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8">
      {/* progress */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s ? "bg-purple-600 text-white" : "bg-white/5 text-zinc-500"}`}>
                {step > s ? <Check size={15} /> : s}
              </span>
              {s < 4 && <span className={`w-6 sm:w-10 h-0.5 ${step > s ? "bg-purple-600" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>
        <button onClick={onCancel} className="text-zinc-500 hover:text-white"><X size={20} /></button>
      </div>

      {step === 2 && <StepInfo w={wizard} />}
      {step === 3 && gc && <StepContent w={{ ...wizard, onBack: () => setStep(2), onNext: () => setStep(4) }} />}

      {step === 4 && resolvedCampaign && (
        <div>
          <h2 className="font-display font-bold text-2xl mb-1">4. Publica tu campaña</h2>
          <p className="text-zinc-400 text-sm mb-6">Sigue los pasos para publicar cumpliendo las reglas anti-spam.</p>
          <PublishPanel campaign={resolvedCampaign} funnelLink={funnelLink} onMethodChange={(m) => setGc((g) => ({ ...g, publish_method: m }))} />
          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep(3)} className="text-zinc-400"><ArrowLeft size={16} className="mr-2" /> Atrás</Button>
            <Button data-testid="finish-wizard" onClick={onDone} className="bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl px-6 py-5">
              <Check size={16} className="mr-2" /> Finalizar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
